import axiosInstance from '../clients/axiosClient';
import { API_V1_PREFIX, PMP_MODALITIES_API_PREFIX } from '../utils/constants';

export interface PmpModality {
  id: number;
  name: string;
  full_price: number | null;
  member_price: number | null;
  is_active: boolean;
}

export interface ListPmpModalitiesResponse {
  modalities: PmpModality[];
  total_count: number;
  limit: number;
  offset: number;
  status: string;
}

export interface CreatePmpModalityRequest {
  name: string;
  full_price?: number | null;
  member_price?: number | null;
}

export interface UpdatePmpModalityRequest {
  id: number;
  name?: string;
  full_price?: number | null;
  member_price?: number | null;
  is_active?: boolean;
}

export interface CreatePmpModalityResponse {
  id: number;
  status: string;
}

export interface UpdatePmpModalityResponse {
  id: number;
  status: string;
}

export const getPmpModalityErrorMessage = (
  error: any,
  fallback = 'Something went wrong. Please try again.'
): string => {
  const data = error?.response?.data;
  const detail = data?.detail ?? data?.description ?? data?.message;

  if (typeof detail === 'string' && detail.trim()) {
    return detail;
  }

  if (Array.isArray(detail) && detail.length > 0) {
    return detail
      .map((item) => item?.msg || item?.message || String(item))
      .filter(Boolean)
      .join(', ');
  }

  if (error instanceof Error && error.message && error.message !== '[object Object]') {
    return error.message;
  }

  return fallback;
};

/**
 * List PMP modalities.
 * Pass is_active=true when populating the PMP Alert modality dropdown.
 */
export const getPmpModalitiesList = async (
  name?: string,
  is_active?: boolean,
  limit: number = 10,
  offset: number = 0
): Promise<ListPmpModalitiesResponse> => {
  try {
    const params: Record<string, string | number | boolean> = {
      limit,
      offset,
    };
    if (name) params.name = name;
    if (is_active !== undefined) params.is_active = is_active;

    const { data } = await axiosInstance.get(
      `${API_V1_PREFIX}${PMP_MODALITIES_API_PREFIX}list`,
      { params }
    );
    return data;
  } catch (error: any) {
    throw new Error(getPmpModalityErrorMessage(error, 'Failed to load PMP modalities'));
  }
};

/** Active modalities for PMP Alert dropdowns. */
export const getActivePmpModalities = async (): Promise<PmpModality[]> => {
  const response = await getPmpModalitiesList(undefined, true, 100, 0);
  return response.modalities ?? [];
};

export const createPmpModality = async (
  request: CreatePmpModalityRequest
): Promise<CreatePmpModalityResponse> => {
  try {
    const { data } = await axiosInstance.post(
      `${API_V1_PREFIX}${PMP_MODALITIES_API_PREFIX}`,
      request
    );
    return data;
  } catch (error: any) {
    throw Object.assign(
      new Error(getPmpModalityErrorMessage(error, 'Failed to create PMP modality')),
      { response: error?.response }
    );
  }
};

export const updatePmpModality = async (
  request: UpdatePmpModalityRequest
): Promise<UpdatePmpModalityResponse> => {
  try {
    const { data } = await axiosInstance.patch(
      `${API_V1_PREFIX}${PMP_MODALITIES_API_PREFIX}`,
      request
    );
    return data;
  } catch (error: any) {
    throw Object.assign(
      new Error(getPmpModalityErrorMessage(error, 'Failed to update PMP modality')),
      { response: error?.response }
    );
  }
};
